// src/features/task/form/AddAction.tsx
// Secondary content lives behind an add-action, not in a permanently open
// section. "+ Checklist" is an offer; the checklist editor appears under the
// row only once it has been accepted.
//
// An add-action is rendered only when the selected task type actually enables
// what it opens, so the row never offers something that cannot be used. Once a
// section holds content the button carries a count — collapsed is never the
// same thing as lost.

import type { ReactNode } from 'react';
import { cn } from '@core/ui';
import { Icon, FORM_ICONS } from './icons';

export interface AddActionProps {
  icon: string;
  label: string;
  /** How much this section already holds. Hidden when zero. */
  count?: number;
  open: boolean;
  onToggle: () => void;
  /** DOM id of the section this button expands. */
  controls: string;
  disabled?: boolean;
}

export function AddAction({
  icon,
  label,
  count,
  open,
  onToggle,
  controls,
  disabled,
}: AddActionProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-expanded={open}
      aria-controls={open ? controls : undefined}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        open ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <Icon d={open ? icon : FORM_ICONS.plus} className="h-3.5 w-3.5 shrink-0" />
      {label}
      {count ? (
        <span className="rounded-full bg-primary/15 px-1.5 text-xs font-medium tabular-nums text-primary">
          {count}
        </span>
      ) : null}
    </button>
  );
}

export interface FormSectionProps {
  id: string;
  title: string;
  /** One short line explaining what happens on create. */
  hint?: string;
  /** Controls aligned right of the heading (manage links). */
  aside?: ReactNode;
  children: ReactNode;
}

/**
 * An expanded add-action: a quiet heading and the editor it opened.
 *
 * No card. In the content column a section is marked by its heading and a
 * hairline above it — the same hairline the add-actions row uses — so a border
 * on screen means one thing only: "this is the other column". Boxing every
 * section made the page read as a stack of unrelated widgets and cost ~24px of
 * padding and rule per open section.
 */
export function FormSection({ id, title, hint, aside, children }: FormSectionProps) {
  return (
    <section id={id} className="border-t border-border pt-3">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {title}
          </h2>
          {hint && <p className="truncate text-xs text-muted-foreground/80">{hint}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}
